import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Bell, User, LogOut, Settings, HelpCircle, 
  ChevronDown, Globe, Menu
} from 'lucide-react';
import { useLogo } from '../../contexts/LogoContext';
import { getUserAvatarUrl } from '../../utils/avatarUtils';
import { toDisplayText } from '../../utils/displayText';
import NotificationDropdown from '../notifications/NotificationDropdown';
import './ModernTopBar.css';

const ModernTopBar = ({ user, onLogout, onMobileMenuToggle, isMobile = false, sidebarWidth = 256 }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logoPreview } = useLogo();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const userMenuRef = useRef(null);
  
  const isRTL = i18n.language === 'ar';
  
  // Initialize RTL on component mount
  useEffect(() => {
    const currentLang = i18n.language;
    const isRTL = currentLang === 'ar';
    
    // Set document direction and language
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLang;
    
    // Add RTL class to body for additional styling
    if (isRTL) {
      document.body.classList.add('rtl');
      document.body.classList.remove('ltr');
    } else {
      document.body.classList.add('ltr');
      document.body.classList.remove('rtl');
    }
  }, [i18n.language]);
  
  // Close menus on outside click
  useEffect(() => {
    if (!showUserMenu) return;

    const handleClickOutside = (event) => {
      if (showUserMenu && userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    
    // Add delay to prevent immediate closing on the click that opens the menu
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);
  
  const handleLanguageChange = (lang) => {
    // Change language
    i18n.changeLanguage(lang);
    
    // Update document direction and language
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    
    // Force re-render to ensure RTL changes are applied
    setForceUpdate(prev => prev + 1);
    
    // Add RTL class to body for additional styling
    if (lang === 'ar') {
      document.body.classList.add('rtl');
      document.body.classList.remove('ltr');
    } else {
      document.body.classList.add('ltr');
      document.body.classList.remove('rtl');
    }
  };
  
  const handleLogout = async () => {
    if (onLogout) {
      await onLogout();
    }
    navigate('/signin');
  };
  
  const lang = i18n.language && i18n.language.startsWith('ar') ? 'ar' : 'en';
  const getUserDisplayName = () => {
    const first = toDisplayText(user?.first_name, lang);
    const last = toDisplayText(user?.last_name, lang);
    if (first && last) return `${first} ${last}`;
    const nameStr = toDisplayText(user?.name, lang);
    if (nameStr) return nameStr;
    if (user?.email) return user.email.split('@')[0];
    return t('common.user', 'User');
  };

  const getUserInitials = () => {
    const first = toDisplayText(user?.first_name, lang);
    const last = toDisplayText(user?.last_name, lang);
    if (first && last) {
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    }
    const nameStr = toDisplayText(user?.name, lang);
    if (nameStr) {
      const nameParts = nameStr.split(' ');
      return nameParts.length > 1 
        ? `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase()
        : nameStr.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  return (
    <header className={`modern-topbar ${isRTL ? 'rtl' : 'ltr'} ${isMobile ? 'mobile' : ''}`} style={{ [isRTL ? 'right' : 'left']: isMobile ? 0 : sidebarWidth }}>
      <div className="topbar-left">
        {/* Mobile Menu Button */}
        {isMobile && onMobileMenuToggle && (
          <button 
            className="mobile-menu-toggle"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMobileMenuToggle();
            }}
            aria-label={t('common.menu', 'Menu')}
            aria-expanded={false}
            type="button"
          >
            <Menu size={24} />
          </button>
        )}
      </div>
      
      <div className="topbar-right">
        {/* Language Toggle Switcher - Compact */}
        <div className="language-toggle-container compact">
          <div className="language-toggle-switcher compact">
            <button
              className={`lang-toggle-btn compact ${i18n.language?.startsWith('en') ? 'active' : ''}`}
              onClick={() => handleLanguageChange('en')}
              aria-pressed={i18n.language?.startsWith('en') || false}
              aria-label={i18n.language?.startsWith('en') ? 'English (current language)' : 'Switch to English'}
            >
              <span className="lang-flag">🇺🇸</span>
              <span className="lang-text">English</span>
            </button>
            <button
              className={`lang-toggle-btn compact ${i18n.language?.startsWith('ar') ? 'active' : ''}`}
              onClick={() => handleLanguageChange('ar')}
              aria-pressed={i18n.language?.startsWith('ar') || false}
              aria-label={i18n.language?.startsWith('ar') ? 'Arabic (current language)' : 'Switch to Arabic'}
            >
              <span className="lang-flag">🇸🇦</span>
              <span className="lang-text">عربي</span>
            </button>
          </div>
        </div>

        {/* Notifications */}
        <NotificationDropdown userId={user?.id} />

        {/* User Profile Menu - Far Right */}
        <div className="user-menu" ref={userMenuRef}>
          <button 
            className="user-profile-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowUserMenu(!showUserMenu);
            }}
            aria-label={t('common.userMenu', 'User Menu')}
          >
            <div className="user-avatar">
              <img
                key={`avatar-${user?.id}-${user?.profile_picture_url || user?.avatar_url || 'default'}`}
                src={getUserAvatarUrl(user)}
                alt={getUserDisplayName()}
                className="avatar-image"
                onError={(e) => {
                  const nameStr = toDisplayText(user?.name, lang);
                  const userName = nameStr || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email || 'User';
                  if (!e.target.src.includes('ui-avatars.com')) {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=667eea&color=fff&size=100`;
                  }
                }}
              />
            </div>
            <span className="user-name">{String(getUserDisplayName() || '')}</span>
            <ChevronDown size={14} />
          </button>
          
          {showUserMenu && (
            <div className="user-dropdown active user-dropdown-menu">
              <button
                className="dropdown-item dropdown-menu-item"
                onClick={() => {
                  navigate('/profile');
                  setShowUserMenu(false);
                }}
              >
                <User size={18} style={{ flexShrink: 0 }} />
                <span>{t('common.profile', 'Profile')}</span>
              </button>

              <div className="dropdown-divider"></div>

              <button
                className="dropdown-item logout-item dropdown-menu-item logout"
                onClick={handleLogout}
              >
                <LogOut size={18} style={{ flexShrink: 0 }} />
                <span>{t('auth.logout', 'Logout')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default ModernTopBar;

